import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useForecast } from '../../hooks/useWeather';
import type { Locale } from 'date-fns';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { getWeatherIcon, translateWeatherDescription } from '../../utils/weather';

interface ForecastDay {
  date: string;
  icon: string;
  temp_max: number;
  temp_min: number;
  precipitation_probability: number;
  humidity: number;
  wind_speed: number;
  description: string;
}

interface ForecastListProps {
  location: { lat: number; lon: number };
}

export default function ForecastList({ location }: ForecastListProps) {
  const { i18n, t } = useTranslation();
  const { data: forecast, isLoading } = useForecast(location.lat, location.lon);
  const locale = i18n.language === 'en' ? enUS : de;

  if (isLoading) {
    return <ForecastSkeleton />;
  }

  if (!forecast?.daily) {
    return null;
  }

  return (
    <div className="space-y-4" data-testid="forecast-list">
      {forecast.daily.slice(0, 7).map((day: ForecastDay, index: number) => (
        <ForecastDay key={day.date} day={day} index={index} locale={locale} t={t} />
      ))}
    </div>
  );
}

function ForecastDay({ day, index, locale, t }: { day: ForecastDay; index: number; locale: Locale; t: any }) {
  const Icon = getWeatherIcon(day.icon);
  const date = new Date(day.date);

  return (
    <motion.div
      className="bg-surface rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-border"
      data-testid="forecast-day"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ scale: 1.02 }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Icon className="w-12 h-12 text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-text-primary">
              {format(date, 'EEEE', { locale })}
            </h3>
            <p className="text-sm text-text-secondary">
              {format(date, 'dd. MMMM', { locale })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="text-2xl font-bold text-text-primary">
              {Math.round(day.temp_max)}°
            </p>
            <p className="text-sm text-text-secondary">
              {Math.round(day.temp_min)}°
            </p>
          </div>

          <div className="text-right">
            <div className="flex items-center gap-1 text-info">
              <span className="text-sm">💧</span>
              <span className="text-sm font-medium">
                {day.precipitation_probability}%
              </span>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-4 text-sm text-text-secondary">
        {translateWeatherDescription(day.description, t)}
      </p>
    </motion.div>
  );
}

function ForecastSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 7 }).map((_, i) => (
        <div
          key={i}
          className="bg-surface rounded-2xl p-6 shadow-lg border border-border"
        >
          <div className="animate-pulse flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-border rounded-full" />
              <div>
                <div className="h-5 w-32 bg-border rounded mb-2" />
                <div className="h-4 w-24 bg-border rounded" />
              </div>
            </div>
            <div className="h-8 w-16 bg-border rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}
